// Prints the recovery codes through the browser's own dialog, which also offers Save as
// PDF. It stays in the browser on purpose: a PDF made by the server would send the codes
// back to the place that only ever keeps their hashes. The frame has real dimensions, off
// screen: a browser can skip printing a frame of no size (components/ui/PrintMenu.tsx).
import { BRAND } from '@/shared/branding';
import { dialog } from '@/shared/store/dialogStore';

// Once the dialog closes the codes leave the page; this is for a browser that never says so.
const REMOVE_AFTER_MS = 60_000;

const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export function recoveryCodesHtml(codes: string[], { account, printedOn }: { account?: string | null; printedOn: string }): string {
  const title = `${BRAND} recovery codes`;
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><title>${esc(title)}</title><style>
@page { margin: 20mm; }
body { font: 14px/1.5 system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; color: #000; margin: 0; }
h1 { font-size: 22px; margin: 0 0 4px; }
.meta { color: #333; margin: 0 0 20px; }
ul { list-style: none; padding: 0; margin: 0 0 20px; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; max-width: 420px; }
li { font: 16px/1.4 ui-monospace, Menlo, Consolas, monospace; border: 1px solid #888; border-radius: 4px; padding: 8px 12px; }
p { margin: 0 0 8px; max-width: 480px; }
</style></head><body>
<h1>${esc(title)}</h1>
<p class="meta">${account ? `${esc(account)} · ` : ''}${esc(printedOn)}</p>
<ul>${codes.map((c) => `<li>${esc(c)}</li>`).join('')}</ul>
<p>Each code signs you in once if you lose your authenticator app.</p>
<p>Keep this page somewhere safe and away from your password. Anyone holding it and your password can get into your account.</p>
</body></html>`;
}

export function printRecoveryCodes(codes: string[], account?: string | null): void {
  const frame = document.createElement('iframe');
  frame.title = 'Recovery codes for printing';
  frame.setAttribute('aria-hidden', 'true');
  frame.style.position = 'fixed';
  frame.style.left = '-10000px';
  frame.style.top = '0';
  frame.style.width = '800px';
  frame.style.height = '600px';
  frame.style.border = '0';
  frame.srcdoc = recoveryCodesHtml(codes, { account, printedOn: new Date().toLocaleDateString() });
  frame.onload = () => {
    const win = frame.contentWindow;
    const timer = window.setTimeout(() => frame.remove(), REMOVE_AFTER_MS);
    const done = () => { window.clearTimeout(timer); frame.remove(); };
    try {
      if (!win) throw new Error('no print frame');
      win.addEventListener('afterprint', done, { once: true });
      win.focus();
      win.print();
    } catch {
      done();
      void dialog.error('The print window could not open. Copy the codes into your password manager instead.');
    }
  };
  document.body.appendChild(frame);
}
